import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Link } from "react-router-dom";
import { Home, Search, ArrowLeft, Ghost } from "lucide-react";
import SEOHead from "../components/SEOHead";

const suggestedPages = [
  { path: "/", labelKey: "nav.home", labelDefault: "Home" },
  { path: "/pricing", labelKey: "nav.products", labelDefault: "Pricing" },
  { path: "/marketplace", labelKey: "nav.marketplace", labelDefault: "Marketplace" },
  { path: "/blog", labelKey: "nav.blog", labelDefault: "Blog" },
  { path: "/docs/api", labelKey: "api_docs.page_title", labelDefault: "API Docs" },
  { path: "/contact", labelKey: "nav.contact", labelDefault: "Contact" },
];

export default function NotFound() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    console.warn(`[404] Page not found: ${location.pathname}${location.search}`);
  }, [location]);

  const filteredPages = searchQuery.trim()
    ? suggestedPages.filter((p) =>
        p.labelDefault.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.path.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : suggestedPages;

  return (
    <div className="min-h-screen bg-[#09090B] text-white flex flex-col">
      <SEOHead
        title={t("not_found.seo_title", { defaultValue: "404 — Page Not Found | Max Employee" })}
        description={t("not_found.seo_desc", { defaultValue: "The page you're looking for doesn't exist." })}
        noIndex
      />

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="text-center max-w-lg w-full">
          {/* Animated 404 */}
          <div
            className={`mb-8 transition-all duration-700 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            <div className="relative inline-block">
              <span className="font-display text-[120px] sm:text-[160px] leading-none text-zinc-900 select-none">
                404
              </span>
              <div className="absolute inset-0 flex items-center justify-center">
                <Ghost className="w-16 h-16 sm:w-20 sm:h-20 text-zinc-700 animate-pulse" strokeWidth={1} />
              </div>
            </div>
          </div>

          <div
            className={`transition-all duration-700 delay-150 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            <h1 className="font-display text-xl sm:text-2xl uppercase tracking-wide mb-3 text-zinc-200">
              {t("not_found.title", { defaultValue: "Page not found" })}
            </h1>
            <p className="text-zinc-500 text-sm font-mono mb-2">
              {t("not_found.path_label", { defaultValue: "Requested:" })}{" "}
              <span className="text-zinc-400 break-all">{location.pathname}</span>
            </p>
            <p className="text-zinc-500 text-sm font-mono mb-8">
              {t("not_found.description", {
                defaultValue: "The page you're looking for doesn't exist or has been moved.",
              })}
            </p>
          </div>

          {/* Search */}
          <div
            className={`mb-8 transition-all duration-700 delay-300 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            <div className="relative max-w-sm mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("not_found.search_placeholder", { defaultValue: "Search pages..." })}
                className="w-full pl-10 pr-4 py-2.5 bg-zinc-900 border border-zinc-800 text-zinc-300 placeholder-zinc-600 font-mono text-sm focus:outline-none focus:border-amber-500/50 transition-colors"
                aria-label={t("not_found.search_placeholder", { defaultValue: "Search pages..." })}
              />
            </div>
          </div>

          {/* Suggested pages */}
          <div
            className={`mb-8 transition-all duration-700 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
            style={{ transitionDelay: "400ms" }}
          >
            <p className="text-zinc-600 text-xs font-mono uppercase tracking-wider mb-3">
              {t("not_found.suggested", { defaultValue: "Suggested pages" })}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {filteredPages.map((page) => (
                <Link
                  key={page.path}
                  to={page.path}
                  className="px-3 py-1.5 border border-zinc-800 hover:border-amber-500/50 text-zinc-400 hover:text-amber-500 font-mono text-xs uppercase tracking-wider transition-colors"
                >
                  {t(page.labelKey, { defaultValue: page.labelDefault })}
                </Link>
              ))}
              {filteredPages.length === 0 && (
                <span className="text-zinc-600 text-xs font-mono">
                  {t("not_found.no_results", { defaultValue: "No matching pages" })}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div
            className={`flex items-center justify-center gap-3 flex-wrap transition-all duration-700 delay-500 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-mono text-xs uppercase tracking-wider transition-colors min-h-[44px]"
            >
              <Home className="w-3.5 h-3.5" />
              {t("not_found.go_home", { defaultValue: "Go Home" })}
            </Link>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-700 hover:border-amber-500 text-zinc-300 hover:text-amber-500 font-mono text-xs uppercase tracking-wider transition-colors min-h-[44px]"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t("not_found.go_back", { defaultValue: "Go Back" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
